import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ExpandToggle } from "@/components/ExpandToggle";
import api from "@/lib/api";
import { eur, num, shortDate, showMoney } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Loader2, Sparkles, ArrowRight } from "lucide-react";

const Thumb = ({ src, alt, big }) => (src
  ? <img src={src} alt={alt} className={`${big ? "h-24 w-[72px]" : "h-16 w-12"} shrink-0 rounded object-cover`} />
  : <div className={`${big ? "h-24 w-[72px]" : "h-16 w-12"} shrink-0 rounded bg-muted`} />);

const Item = ({ p, size }) => (
  <div className="flex gap-2.5 rounded-md border border-border/60 bg-background p-2" data-testid={`reco-item-${p.id}`}>
    <Thumb src={p.image_url} alt={p.name} />
    <div className="min-w-0 flex-1 text-sm">
      <p className="line-clamp-2 text-[13px] font-medium leading-tight">{p.name}</p>
      <p className="mt-0.5 text-xs text-muted-foreground">{p.color}{showMoney() && ` · ${eur(p.price)}`} {p.season && <Badge variant="outline" className="ml-1 text-[10px]">{p.season}</Badge>}</p>
      <p className="mt-0.5 text-[11px]">
        {size && p.size_qty != null
          ? <span className={p.size_qty > 0 ? "text-emerald-400" : "text-rose-400"} data-testid={`reco-size-${p.id}`}>Taglia {size}: {p.size_qty > 0 ? `${num(p.size_qty)} disp.` : "esaurita"}</span>
          : <span className="text-muted-foreground">{num(p.stock)} pz disponibili</span>}
      </p>
    </div>
  </div>
);

const Group = ({ g, size }) => (
  <div className="rounded-md border border-border/60 p-3" data-testid={`reco-group-${g.gid}`}>
    <div className="grid grid-cols-1 items-start gap-3 md:grid-cols-[200px_auto_1fr]">
      <div className="flex gap-2.5">
        <Thumb src={g.image_url} alt={g.bought} big />
        <div className="min-w-0 text-sm">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Ha comprato</p>
          <p className="line-clamp-3 font-semibold leading-tight">{g.bought}</p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">{[g.color, g.size && `tg ${g.size}`, shortDate(g.date)].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      <div className="hidden items-center gap-1 self-center text-[11px] text-primary md:flex"><ArrowRight className="h-4 w-4" />abbinale</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">{g.items.map((p) => <Item key={p.id} p={p} size={size} />)}</div>
    </div>
  </div>
);

export const CustomerRecommendations = ({ id }) => {
  const [all, setAll] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["customer-reco", id], enabled: !!id,
    queryFn: async () => (await api.get(`/customers/${id}/recommendations`)).data });
  const groups = data?.groups || [];
  return (
    <div data-testid="customer-recommendations">
      <p className="mb-1 flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5 text-primary" />Capi da abbinare a quello che ha già comprato
        {data?.fav_size && <span className="normal-case tracking-normal">· taglia abituale {data.fav_size}</span>}
      </p>
      <p className="mb-3 text-xs text-muted-foreground">A sinistra il capo acquistato (dal più recente), a destra i capi disponibili che si abbinano, secondo gli abbinamenti impostati su Shopify.</p>
      {isLoading ? <Loader2 className="h-4 w-4 animate-spin text-primary" /> : (
        <div className="space-y-2">
          {(all ? groups : groups.slice(0, 4)).map((g) => <Group key={g.gid} g={g} size={data.fav_size} />)}
          {!groups.length && <p className="text-sm text-muted-foreground" data-testid="reco-empty">
            {data.links_synced ? "Nessun capo da abbinare disponibile per quello che ha acquistato." : "Abbinamenti non ancora sincronizzati: premi \"Sincronizza prodotti\"."}</p>}
        </div>
      )}
      {data && <ExpandToggle total={groups.length} open={all} onToggle={() => setAll(!all)} limit={4} testid="reco-toggle" />}
    </div>
  );
};
